import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export const scrollToId = (id) => {
  const el = document.querySelector(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const onDark = !scrolled && !menuOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setMenuOpen(false);
    if (location.pathname !== "/") {
      window.location.href = `/${id}`;
      return;
    }
    scrollToId(id);
  };

  const links = [
    { id: "#apartamentos", label: t("nav.apartments") },
    { id: "#mapa", label: t("nav.map") },
    { id: "#contacto", label: t("nav.contact") },
  ];

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-sand/90 backdrop-blur-md border-b border-line" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[70px] flex items-center justify-between">
          <Link
            to="/"
            data-testid="brand-link"
            className={`font-serif text-xl md:text-2xl tracking-tight transition-colors duration-500 ${
              onDark ? "text-sand" : "text-ink"
            }`}
          >
            Luz de <span className="italic">Cádiz</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id.slice(1)}`}
                onClick={() => go(l.id)}
                className={`text-xs uppercase tracking-[0.2em] transition-colors duration-500 hover:text-terracotta ${
                  onDark ? "text-sand/90" : "text-ink/70"
                }`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LanguageSwitcher dark={onDark} />
            <button
              data-testid="nav-book-btn"
              onClick={() => go("#apartamentos")}
              className={`hidden md:inline-block px-6 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                onDark
                  ? "bg-sand text-ink hover:bg-terracotta hover:text-sand"
                  : "bg-ink text-sand hover:bg-terracotta"
              }`}
            >
              {t("nav.book")}
            </button>
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMenuOpen(!menuOpen)}
              className={`lg:hidden p-2 transition-colors ${onDark ? "text-sand" : "text-ink"}`}
              aria-label={t("nav.menu")}
            >
              {menuOpen ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-sand pt-28 px-8 lg:hidden"
          >
            <div className="flex flex-col gap-2">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id.slice(1)}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i }}
                  onClick={() => go(l.id)}
                  className="text-left font-serif text-4xl py-4 border-b border-line text-ink"
                >
                  {l.label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
